import axios from "axios";

/**
 * 正式模考的「中途離開」偵測。
 *
 * Edric 要的是嚴格模式：離開就鎖住，要教務手動解鎖。但瀏覽器分不出
 * 「作弊去查答案」和「手機來電／螢幕休眠／通知跳出來」——`visibilitychange`
 * 這些情況全都會觸發。真實考場不會因為一則通知就作廢一場考試，程式也不該。
 *
 * 所以做法是：
 *   - 只有「頁面真的隱藏超過 GRACE_MS」才算離開，短暫失焦不算 → 會鎖
 *   - 關閉分頁或重新整理只記次數、不鎖：瀏覽器在 pagehide 的當下分不出
 *     這兩件事，而把只是按了 F5 的學生鎖在門外，代價比放過「關掉分頁去
 *     查東西」高——後者回來還是得從 dashboard 重新輸密碼，而且次數會留紀錄
 *   - 不管有沒有鎖到，伺服器都會累計 leave_count，教務看得到發生過幾次
 *
 * 自我練習不呼叫這支——伺服器端也會再判斷一次模式，不會誤鎖。
 */

/** 隱藏多久才算真的離開 */
export const GRACE_MS = 5000;

export function reportLeave(examDetailId, reason) {
  const url = `${process.env.REACT_APP_EXAM_DETAILS}/${examDetailId}/leave`;
  return axios.post(url, { reason });
}

/**
 * 關閉分頁時用 sendBeacon——那個當下瀏覽器不保證 XHR 送得出去。
 * 沒有 sendBeacon 的舊瀏覽器就放棄這一次（回來時的 visibilitychange 仍會補記）。
 *
 * 帶 lock=0：只記次數不鎖。pagehide 分不出「關掉分頁」與「重新整理」。
 */
export function reportLeaveBeacon(examDetailId, reason) {
  const url = `${process.env.REACT_APP_EXAM_DETAILS}/${examDetailId}/leave`;
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    const body = new FormData();
    body.append("reason", reason);
    body.append("lock", "0");
    return navigator.sendBeacon(url, body);
  }
  return false;
}

/**
 * 掛上監聽，回傳解除監聽的函式。
 *
 * @param {object} opts
 * @param {number|string} opts.examDetailId
 * @param {function} opts.onLocked  伺服器回報已鎖定時呼叫（畫面要擋起來）
 * @param {number}   opts.graceMs
 */
export function watchForLeave({ examDetailId, onLocked, graceMs = GRACE_MS }) {
  let hiddenAt = null;
  let done = false;

  const handleVisibility = () => {
    if (done) {
      return;
    }
    if (document.hidden) {
      hiddenAt = Date.now();
      return;
    }
    // 回到畫面：看剛剛離開多久
    if (hiddenAt === null) {
      return;
    }
    const away = Date.now() - hiddenAt;
    hiddenAt = null;
    if (away < graceMs) {
      return;                       // 通知、來電這種一瞬間的不算
    }
    done = true;
    const reason = "切換分頁或離開畫面";
    reportLeave(examDetailId, reason)
      .then((res) => {
        if (res.data && res.data.locked) {
          onLocked({ ...res.data, reason });
        } else {
          done = false;             // 沒鎖（自我練習）就繼續監看
        }
      })
      .catch(() => {
        done = false;
      });
  };

  const handlePageHide = () => {
    if (done) {
      return;
    }
    reportLeaveBeacon(examDetailId, "關閉或重新整理頁面");
  };

  document.addEventListener("visibilitychange", handleVisibility);
  window.addEventListener("pagehide", handlePageHide);

  return () => {
    document.removeEventListener("visibilitychange", handleVisibility);
    window.removeEventListener("pagehide", handlePageHide);
  };
}
